import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query'

/**
 * AD-202: one tier at a time.
 *
 * An integration offers as many tiers as it sells and names them itself, so tiers are
 * created, edited and removed individually -- the same shape as packages.
 */

// A refused tier says why: the first rule it broke, not Laravel's "The given data was invalid."
const notifyError = ({ response }) => notifications.show({
    color: 'red',
    title: 'Error',
    message: Object.values(response?.data?.errors || {})[0]?.[0] || response?.data?.message || '',
});

const notifySuccess = (message) => notifications.show({
    color: 'green',
    title: 'Success',
    message,
});

/**
 * The booking flow reads the tier list through its own cached query, and the client is
 * configured with refetchOnWindowFocus off. Without dropping that cache here, a tier
 * that was just switched off, renamed or removed went on being offered in the booking
 * flow until the cache happened to expire.
 *
 * Matched by key prefix rather than by id: the booking flow takes the integration id
 * from the campaign rather than from this form, and a type mismatch between the two
 * would leave the stale list quietly in place.
 */
const invalidateTiers = (queryClient) => queryClient.invalidateQueries({
    queryKey: ['api-v1-studio-get-integration-tiers'],
});

export const mutateTierStore = ({ integrationId }) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['integration-tier-store', integrationId],
        mutationFn: async (formData) => axios.post(
            route('studio.v1.api.integrations.tiers.store', integrationId),
            formData,
        ),
        onSuccess: async () => {
            await invalidateTiers(queryClient);
            notifySuccess('Tier created');
        },
        onError: notifyError,
    });
};

export const mutateTierUpdate = ({ integrationId, tierId }) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['integration-tier-update', integrationId, tierId],
        mutationFn: async (formData) => axios.put(
            route('studio.v1.api.integrations.tiers.update', [integrationId, tierId]),
            formData,
        ),
        onSuccess: async () => {
            await invalidateTiers(queryClient);
            notifySuccess('Tier saved');
        },
        onError: notifyError,
    });
};

export const mutateTierDelete = ({ integrationId, tierId }) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['integration-tier-delete', integrationId, tierId],
        mutationFn: async () => axios.delete(
            route('studio.v1.api.integrations.tiers.destroy', [integrationId, tierId]),
        ),
        onSuccess: async () => {
            await invalidateTiers(queryClient);
            notifySuccess('Tier deleted');
        },
        onError: notifyError,
    });
};
